import { Enums } from '@bode-canada/core';
import type { ApexOptions } from 'apexcharts';
import merge from 'deepmerge';
import {
  buildChart, buildCSVExport, CURRENCY_FORMATTER, DEFAULT_OPTIONS,
} from '@/components/shared/chart/chartOptions';

const buildYAxis = (name: string, yAxis: ApexYAxis = {}): ApexYAxis => merge({
  seriesName: name,
  title: {
    text: name,
    style: {
      color: '#009fd4',
    },
  },
  axisTicks: {
    show: true,
  },
  axisBorder: {
    show: true,
    color: '#009fd4',
  },
  labels: {
    formatter: (value) => `${value}`,
  },
}, yAxis);

const WEEKLY_VIEWS_OPTIONS: ApexOptions = {
  ...DEFAULT_OPTIONS,
  ...buildChart({
    id: 'Weekly Bode Views Graph',
    ...buildCSVExport({
      filename: 'Weekly Bode Views Spreadsheet',
    }),
  }),
  yaxis: [
    buildYAxis('Number of views'),
  ],
};

const SOLD_PRICE_PER_SQFT_OPTIONS: ApexOptions = {
  ...DEFAULT_OPTIONS,
  ...buildChart({
    id: 'Price per Sqft Graph',
    ...buildCSVExport({
      filename: 'Price per Sqft Spreadsheet',
    }),
  }),
  yaxis: [
    buildYAxis('Average Price Per Sqft', { labels: { formatter: CURRENCY_FORMATTER } }),
  ],
};

const DAYS_ON_MARKET_OPTIONS: ApexOptions = {
  ...DEFAULT_OPTIONS,
  ...buildChart({
    id: 'Days on Market Graph',
    ...buildCSVExport({
      filename: 'Days on Market Spreadsheet',
    }),
  }),
  yaxis: [
    buildYAxis('Average Days on Market'),
  ],
};

const NUMBER_OF_NEW_LISTINGS_OPTIONS: ApexOptions = {
  ...DEFAULT_OPTIONS,
  ...buildChart({
    id: 'New Listing Volume Graph',
    ...buildCSVExport({
      filename: 'New Listing Volume Spreadsheet',
    }),
  }),
  yaxis: [
    buildYAxis('Number of New Listings', { labels: { formatter: (value) => (value < 1 ? '0' : `${value}`) } }),
  ],
};

const NUMBER_OF_SALES_OPTIONS: ApexOptions = {
  ...DEFAULT_OPTIONS,
  ...buildChart({
    id: 'Number of Sales Graph',
    ...buildCSVExport({
      filename: 'Number of Sales Spreadsheet',
    }),
  }),
  yaxis: [
    buildYAxis('Number of Sold Listings'),
  ],
};

const SOLD_PRICE_OPTIONS: ApexOptions = {
  ...DEFAULT_OPTIONS,
  ...buildChart({
    id: 'Sales Trend Graph',
    ...buildCSVExport({
      filename: 'Sales Trend Spreadsheet',
    }),
  }),
  stroke: {
    width: [0, 2],
  },
  yaxis: [
    buildYAxis('Sale Price Average', { labels: { formatter: CURRENCY_FORMATTER } }),
    buildYAxis('Sold Price / List Price Ratio', {
      title: {
        style: {
          color: '#0055d4',
        },
      },
      axisBorder: {
        color: '#0055d4',
      },
      labels: { formatter: (value) => `${value}%` },
      forceNiceScale: true,
      opposite: true,
      max: 110,
      min: 80,
    }),
  ],
};

const CHART_TYPE_OPTIONS_MAP: Record<Enums.ChartTypeEnum, ApexOptions> = {
  [Enums.ChartTypeEnum.WEEKLY_VIEWS]: WEEKLY_VIEWS_OPTIONS,
  [Enums.ChartTypeEnum.SOLD_PRICE_PER_SQFT]: SOLD_PRICE_PER_SQFT_OPTIONS,
  [Enums.ChartTypeEnum.DAYS_ON_MARKET]: DAYS_ON_MARKET_OPTIONS,
  [Enums.ChartTypeEnum.NUMBER_OF_NEW_LISTINGS]: NUMBER_OF_NEW_LISTINGS_OPTIONS,
  [Enums.ChartTypeEnum.NUMBER_OF_SALES]: NUMBER_OF_SALES_OPTIONS,
  [Enums.ChartTypeEnum.SOLD_PRICE]: SOLD_PRICE_OPTIONS,
};

const getMarketDataChartOptions = (
  chartType: Enums.ChartTypeEnum,
  categories: string[],
): ApexOptions => merge<ApexOptions>({
  xaxis: {
    categories,
  },
}, CHART_TYPE_OPTIONS_MAP[chartType]);

export default getMarketDataChartOptions;
